/**
 * @author gll & lifei
 * update: 2024-9-29 未做特殊处理仅限于全局水印，后续根据需求调整逻辑
 */
import * as func from '@/func'
import { Base } from '../base'

type TOnCreateTime = 'on' | 'off'
type TParams = [string, TOnCreateTime, HTMLElement?]

type TOptions = {
  id: string
  // 水印字体
  font?: string
  // 水印字体颜色
  color?: string
  // 画布宽
  width?: number
  // 画布高
  height?: number
  // 旋转角度
  angle?: number
}

export class Watermark extends Base {
  constructor(options?: TOptions) {
    super()
    Object.assign(this.defaultOptions, options || {})
  }

  static {
    Base.register(this)
  }

  /**
   * 功能：默认参数配置
   */
  private defaultOptions = {
    // 唯一标识
    id: '1.23452384164.123412416',
    width: 400,
    height: 300,
    angle: -15,
    color: 'rgba(200, 200, 200, 0.40)',
    font: '14px Vedana',
  }

  /**
   * 功能：画布上下文
   */
  private ctx?: TCommon.OrNull<CanvasRenderingContext2D> = null

  /**
   * 功能：画布元素实例
   */
  private canvas: TCommon.OrNull<HTMLCanvasElement> = null

  /**
   * 功能：水印呈现元素
   */
  private container: TCommon.OrNull<HTMLDivElement> = null

  /**
   * 功能：接收定时器 id，暂未启用
   */
  private timer: TCommon.OrNull<ReturnType<typeof window.setInterval>> = null

  /**
   * 功能：
   */
  private observer: TCommon.OrNull<ResizeObserver> = null

  /**
   * 功能：绘制水印
   */
  public draw = async (...args: TParams) => {
    const [content] = args
    if (!content) return

    this.observer = new ResizeObserver(() => {
      this.render(...args)
    })
    await func.delay(50)
    this.render(...args)
  }

  /**
   * 功能：销毁画布和监听等
   */
  public destroy = () => {
    this.observer?.disconnect()
    this.canvas = null
    this.container = null
    this.timer && clearInterval(this.timer)
  }

  /**
   * 功能：创建画布容器，即展示水印背景容器
   */
  private createContainer = (): HTMLDivElement => {
    const { id } = this.defaultOptions
    const div = document.createElement('div')
    div.id = id
    return div
  }

  /**
   * 功能：创建画布
   */
  private createCanvas = (): HTMLCanvasElement => {
    const { width, height } = this.defaultOptions
    const canvas = document.createElement('canvas')
    Object.assign(canvas, {
      height,
      width,
    })
    return canvas
  }

  /**
   * 功能：获取创建时间
   */
  private getCreateTime = (): string => {
    const curDate = new Date()
    const dateStr = curDate.toLocaleDateString()
    const timeStr = curDate.toLocaleTimeString()
    return [dateStr, timeStr].join(' ')
  }

  /**
   * 功能： 绘制内容，核心逻辑
   * @param content: 水印内容
   * @param box 容器
   * @param onCreateTime 水印创建时间，off 不启动时间；on 启动水印创建时间
   */
  private render = async (content: string, onCreateTime: TOnCreateTime = 'off', box?: HTMLElement) => {
    const { color, font, angle, width, height, id } = this.defaultOptions

    const showCreateTime = onCreateTime == 'on'
    const container = document.getElementById(id)
    container && document.body.removeChild(container)
    this.container = this.createContainer()

    if (!this.canvas) {
      this.canvas = this.createCanvas()
    }

    this.ctx = this.canvas.getContext('2d')

    if (this.ctx) {
      this.ctx.clearRect(0, 0, width, height)

      if (showCreateTime) {
        const createTime = this.getCreateTime()
        const timeTextWidth = this.ctx.measureText(createTime).width
        this.ctx.font = '14px Vedana'
        this.ctx.fillText(createTime, (this.canvas.width - timeTextWidth) / 2, (this.canvas.height / 3) * 2)
      } else {
        this.ctx.rotate((angle * Math.PI) / 180)
        this.ctx.font = font
        this.ctx.fillStyle = color
        this.ctx.textBaseline = 'middle'
        this.ctx.fillText(content, this.canvas.width / 8, this.canvas.height / 2)
      }

      const imgUrl = this.canvas.toDataURL('image/png', 0.9)

      Object.assign(this.container.style, {
        pointerEvents: 'none',
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        position: 'absolute',
        zIndex: '999999',
        width: box ? box.clientWidth + 'px' : '100%',
        height: box ? box.clientHeight + 'px' : '100%',
        background: 'url(' + imgUrl + ') left top repeat',
      })

      if (box) {
        // 给指定盒子添加水印
        box.style.position = 'relative'
        this.container && box.appendChild(this.container)
      } else {
        this.container && document.body.appendChild(this.container)
      }
    } else {
      throw new Error('Failed to get the 2D context of the canvas.')
    }
    return this.defaultOptions.id
  }

  /**
   * 功能：重绘, 未实现
   */
  private reRender = () => {}
}

export const watermark = new Watermark()
